import React, { useState, useEffect, useContext } from "react";
import { Box, Grow, SxProps, Theme } from "@mui/material";
import { FormsListItemCard } from "./FormsListItemCard";
import { AtticusForm } from "../../../../models/AtticusForm";
import { EstateFormsContext } from "EstateFormsContextWrapper";
import { SplitButtonOption } from "views/shared/SplitButtonGroup";
import AtticusFormViewDialog from "views/shared/dialog/AtticusFormViewDialog";
import { DetailsDialog } from "views/shared/dialog/DetailsDialog";
import { Estate } from "models/Estate";
import { PdfFileChoice } from "views/shared/dialog/useAtticusFormViewer";
import { useAuthContext } from "AuthContextWrapper"
import AtticusFormUpgradeToViewDialog from "views/shared/dialog/AtticusFormUpgradeToViewDialog";
import AtticusFormConfirmRemoveDialog from "views/shared/dialog/AtticusFormConfirmRemoveDialog";
import { Loading } from "views/shared/Loading";
import { LARAVEL_APP_HOST } from "../../../../constants";
import formDetailsIcon from "../../../../assets/form-details-icon.svg";
import { downloadAtticusForm } from "utils/download";

interface Props {
  forms?: AtticusForm[];
  autoloadForm?: AtticusForm;
  estate?: Estate;
  sx?: SxProps<Theme>;
  truncateFormsAt?: number;
  emptyState?: JSX.Element;
  allowCheckedBadge?: boolean;
}

export const FormsList = ({
  forms,
  autoloadForm,
  estate,
  sx,
  truncateFormsAt,
  emptyState,
  allowCheckedBadge = false,
}: Props): JSX.Element => {
  const { estateForms, addEstateForm, fetchEstateForms, estateFormsIsLoading } = useContext(EstateFormsContext);
  const { subscription, user, activeEstate } = useAuthContext()
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(!!autoloadForm);
  const [formToRemove, setFormToRemove] = useState<AtticusForm | undefined>(undefined);
  const [pdfDialogForm, setPdfDialogForm] = useState<AtticusForm | undefined>(autoloadForm);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [descriptionDialogForm, setDescriptionDialogForm] = useState<AtticusForm>();

  const togglePdfDialog = () => {
    setIsPdfDialogOpen(!isPdfDialogOpen);
    if (isPdfDialogOpen === true) {
      setPdfDialogForm(undefined);
    }
  };
  const toggleDescriptionDialog = () => {
    setDescriptionDialogOpen(!descriptionDialogOpen);
    if (descriptionDialogOpen === true) {
      setDescriptionDialogForm(undefined);
    }
  };
  const redirectToAfterUpgrade = encodeURIComponent(
    window.location.protocol +
    "//" +
    window.location.host +
    "/app/forms-and-files"
  );

  const onRemovedForm = () => {
    setFormToRemove(undefined);
    if (estate?.id) {
      fetchEstateForms(estate.id);
    }
  };


  useEffect(() => {
    if(forms) {
      return
    }

    if(!estateForms && estate?.id && fetchEstateForms) {
      fetchEstateForms(estate.id);
    }
  }, [estateForms, fetchEstateForms, estate]);

  const getFormOptions = (form: AtticusForm): SplitButtonOption[] => {
    const formOptions: SplitButtonOption[] = [];

    if (subscription?.isActive) {
      formOptions.push({
        label: "View",
        key: "view",
        onClick: async () => {
          if (form) {
            setPdfDialogForm(form);
            togglePdfDialog();
          }
        },
      });

      formOptions.push({
        label: "Download",
        key: "download",
        onClick: async () => {
          if (form && activeEstate?.id && user?.id) {
            downloadAtticusForm(activeEstate?.id, user?.id, form);
          }
        },
      });
    } else {
      formOptions.push({
        label: "Upgrade",
        key: "upgrade",
        onClick: async () => {
          window.location.href = `${LARAVEL_APP_HOST}/payment-plan?redirectToAfterUpgrade=${redirectToAfterUpgrade}`;
        },
      });
    }

    if (estate && estateForms?.find((estateForm) => estateForm.id == form.id)) {
      formOptions.push({
        label: "Remove from My Forms",
        key: "remove",
        hidden: !estateForms || !estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && estate.id) {
            setFormToRemove(form);
          }
        },
      });
    } else if (estate && estateForms) {
      formOptions.push({
        label: "Add to My Forms",
        key: "add",
        hidden: !estateForms || !!estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && estate.id && user?.id) {
            addEstateForm(estate.id, user.id, form);
            fetchEstateForms(estate.id);
          }
        },
      });
    }

    return formOptions;
  };

  const renderForms = (): JSX.Element[] => {
    const rows = [];
    const estateFormsMap:{[key: string]: AtticusForm} = {}
    estateForms?.map(ef => estateFormsMap[ef.id.toString()] = ef)
    const formsToDisplay = forms?.map(f => {
      return estateFormsMap[f.id.toString()] || f
    })
    if (!formsToDisplay) {
      for (let formI = 0; formI < 3; formI++) {
        rows.push(
          <Grow in key={formI} timeout={formI * 100}>
            <Box>
              <FormsListItemCard
                pdfDialogOpen={isPdfDialogOpen}
                setPdfDialogForm={setPdfDialogForm}
                togglePdfDialog={togglePdfDialog}
                descriptionDialogOpen={descriptionDialogOpen}
                setDescriptionDialogForm={setDescriptionDialogForm}
                toggleDescriptionDialog={toggleDescriptionDialog}
                sx={{
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              />
            </Box>
          </Grow>
        );
      }
    } else {
      (truncateFormsAt ? formsToDisplay.slice(0, truncateFormsAt) : formsToDisplay).forEach(
        (form, index) => {
          rows.push(
            <Grow in key={form.id + "_" + index} timeout={index * 100}>
              <Box>
                <FormsListItemCard
                  form={form}
                  estate={estate}
                  pdfDialogOpen={isPdfDialogOpen}
                  setPdfDialogForm={setPdfDialogForm}
                  togglePdfDialog={togglePdfDialog}
                  descriptionDialogOpen={descriptionDialogOpen}
                  setDescriptionDialogForm={setDescriptionDialogForm}
                  toggleDescriptionDialog={toggleDescriptionDialog}
                  sx={{
                    padding: "1rem",
                    marginBottom: "1rem",
                  }}
                  formOptions={getFormOptions(form)}
                  checkedBadge={
                    allowCheckedBadge && form.estateFormMap ? true : false
                  }
                />
              </Box>
            </Grow>
          );
        }
      );
    }
    return rows;
  };

  

  return (
    <Box sx={sx}>
      {subscription?.isActive ? (
        <AtticusFormViewDialog
          open={isPdfDialogOpen}
          onClose={togglePdfDialog}
          form={pdfDialogForm}
          fileChoice={PdfFileChoice.PROCESSED}
          withDisclaimer
        />
      ) : (
        <AtticusFormUpgradeToViewDialog
          open={isPdfDialogOpen}
          onClose={togglePdfDialog}
          form={pdfDialogForm}
        />
      )}
      {formToRemove && estate?.id && user?.id ? (
        <AtticusFormConfirmRemoveDialog
          open={!!formToRemove}
          onClose={onRemovedForm}
          form={formToRemove}
          estateId={estate?.id}
          userId={user?.id} 
        />
      ) : null}
      <DetailsDialog
        open={descriptionDialogOpen}
        headerImageSrc={formDetailsIcon}
        headerImageAlt="A magnifying glass observing a form's text"
        title={descriptionDialogForm?.formTitle || "Form Details"}
        subtitle={`${descriptionDialogForm?.geography?.fullName
          ? descriptionDialogForm?.geography?.fullName
          : ""
          }${descriptionDialogForm?.geography?.fullName &&
            descriptionDialogForm?.formGovernmentId
            ? " | "
            : ""
          }${descriptionDialogForm?.formGovernmentId
            ? descriptionDialogForm?.formGovernmentId
            : ""
          }`}
        content={
          descriptionDialogForm?.formDescription || "No Description Available."
        }
        onConfirm={toggleDescriptionDialog}
        confirmText="Ok"
        maxWidth="md"
      />
      <Box>
        {!estateFormsIsLoading && forms?.length ? (
          renderForms()
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              padding: 4,
            }}
          >
            {estateFormsIsLoading ? (
              <Loading />
            ) : (!forms?.length) ? (
              <>{emptyState}</>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
};
